import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { getFileType } from "@repo/design-system/components/ui";

// Helpers

const decodeBase64Text = (base64) => {
    try {
        // Strip data URI prefix if present e.g. data:text/plain;base64,
        const raw = base64.includes(",") ? base64.split(",")[1] : base64;
        return atob(raw);
    } catch {
        return "Failed to decode file content.";
    }
};

const getBase64Raw = (base64) =>
    base64.includes(",") ? base64.split(",")[1] : base64;

const base64ToArrayBuffer = (base64) => {
    const raw = getBase64Raw(base64);
    const binary = atob(raw);
    const buffer = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
    return buffer.buffer;
};

//File Type Renderers
const PDFViewer = ({ src }) => (
    <iframe src={src} title="PDF Viewer" width="100%"
        height="600px" style={{ border: "none" }} />
);
const ImageViewer = ({ src, fileName }) => (
    <img src={src} alt={fileName}
        style={{ width: "100%", height: "auto", objectFit: "contain" }} />
);
const TextViewer = ({ content, language }) => (
    <pre style={{
        background: "#1e1e1e", color: "#d4d4d4",
        padding: "16px", borderRadius: "8px",
        overflowX: "auto", fontSize: "13px",
        whiteSpace: "pre-wrap", wordBreak: "break-word",
        maxHeight: "600px", overflowY: "auto",
    }}>
        {language === "json"
            ? JSON.stringify(JSON.parse(content), null, 2)
            : content
        }
    </pre>
);
const CSVViewer = ({ content }) => {
    const rows = content.trim().split("\n").map((r) => r.split(","));
    const headers = rows[0];
    const data = rows.slice(1);

    return (
        <div style={{ overflowX: "auto", maxHeight: "600px", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                    <tr>
                        {headers.map((h, i) => (
                            <th key={i} style={{
                                padding: "8px 12px", background: "#f0f0f0",
                                border: "1px solid #ddd", textAlign: "left",
                                position: "sticky", top: 0,
                            }}>
                                {h.trim()}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                            {row.map((cell, j) => (
                                <td key={j} style={{ padding: "8px 12px", border: "1px solid #ddd" }}>
                                    {cell.trim()}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
const HTMLViewer = ({ content }) => (
    <iframe
        srcDoc={content}
        title="HTML Viewer"
        sandbox="allow-same-origin"
        width="100%"
        height="600px"
        style={{ border: "1px solid #ddd", borderRadius: "8px" }}
    />
);
const XLSXViewer = ({ base64Data }) => {
    const [html, setHtml] = useState("");

    useEffect(() => {
        try {
            const buffer = base64ToArrayBuffer(base64Data);
            const workbook = XLSX.read(buffer, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            setHtml(XLSX.utils.sheet_to_html(sheet));
        } catch {
            setHtml("<p>Failed to load spreadsheet.</p>");
        }
    }, [base64Data]);

    return (
        <div
            dangerouslySetInnerHTML={{ __html: html }}
            style={{
                overflowX: "auto", maxHeight: "600px",
                overflowY: "auto", fontSize: "13px",
            }}
        />
    );
};
const DOCXViewer = ({ base64Data }) => {
    const [html, setHtml] = useState("");
    const [error, setError] = useState(false);

    useEffect(() => {
        const convert = async () => {
            try {
                const mammoth = await import("mammoth");
                const buffer = base64ToArrayBuffer(base64Data);
                const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
                setHtml(result.value);
            } catch {
                setError(true);
            }
        };
        convert();
    }, [base64Data]);

    if (error) return <DownloadFallback />;

    return (
        <div
            dangerouslySetInnerHTML={{ __html: html }}
            style={{
                padding: "24px", maxHeight: "600px",
                overflowY: "auto", fontSize: "14px",
                lineHeight: "1.6", background: "#fff",
                border: "1px solid #ddd", borderRadius: "8px",
            }}
        />
    );
};
const ZIPViewer = ({ base64Data, fileName }) => {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState(false);

    useEffect(() => {
        const readZip = async () => {
            try {
                const buffer = base64ToArrayBuffer(base64Data);
                const zip = await JSZip.loadAsync(buffer);
                setFiles(Object.keys(zip.files));
            } catch {
                setError(true);
            }
        };
        readZip();
    }, [base64Data]);

    if (error) return <DownloadFallback fileName={fileName} base64Data={base64Data} />;

    return (
        <div style={{ padding: "16px" }}>
            <p style={{ fontWeight: 600, marginBottom: "12px" }}>
                📦 Archive Contents ({files.length} files)
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {files.map((f, i) => (
                    <li key={i} style={{
                        padding: "6px 10px", fontSize: "13px",
                        borderBottom: "1px solid #f0f0f0",
                        display: "flex", alignItems: "center", gap: "8px",
                    }}>
                        📄 {f}
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Download Fallback
const DownloadFallback = ({ fileName, base64Data, message }) => (
    <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "40px", textAlign: "center", gap: "16px",
    }}>
        <span style={{ fontSize: "48px" }}>📄</span>
        <p style={{ color: "#555", margin: 0 }}>
            {message || "Preview is not available for this file type."}
        </p>
        {fileName && base64Data && (
            <a
                href={base64Data}
                download={fileName}
                style={{
                    display: "inline-block", padding: "10px 24px",
                    background: "#1a73e8", color: "#fff",
                    borderRadius: "6px", textDecoration: "none",
                    fontSize: "14px", fontWeight: 500
                }} >
                Download {fileName}
            </a>
        )}
    </div>
);

// Main Component

export const DocumentViewer = ({ documentData }) => {
    const fileSource = documentData?.base64Data;
    const fileName = documentData?.file?.fileName || documentData?.file?.name;

    if (!fileSource || !fileName) {
        return <p style={{ padding: "16px", color: "#888" }}>No document available.</p>;
    }

    const fileType = getFileType(fileName); // "PDF" | "IMAGE" | "DOCX" | "XLSX" | etc.
    const ext = fileName.split(".").pop().toLowerCase();

    // PDF
    if (fileType === "PDF") return <PDFViewer src={fileSource} />;

    //  IMAGE 
    if (fileType === "IMAGE") return <ImageViewer src={fileSource} fileName={fileName} />;

    //  DOCX / DOC 
    if (["docx", "doc"].includes(ext)) return <DOCXViewer base64Data={fileSource} />;

    //  XLSX / XLS 
    if (["xlsx", "xls"].includes(ext)) return <XLSXViewer base64Data={fileSource} />;

    //  CSV 
    if (ext === "csv") return <CSVViewer content={decodeBase64Text(fileSource)} />;

    //  HTML 
    if (ext === "html") return <HTMLViewer content={decodeBase64Text(fileSource)} />;

    //  JSON 
    if (ext === "json") {
        return <TextViewer content={decodeBase64Text(fileSource)} language="json" />;
    }

    //  Plain Text (TXT, MD, LOG, RTF, XML) 
    if (["txt", "md", "log", "rtf", "xml"].includes(ext)) {
        return <TextViewer content={decodeBase64Text(fileSource)} />;
    }

    //  ZIP / RAR 
    if (ext === "zip") return <ZIPViewer base64Data={fileSource} fileName={fileName} />;

    //  PPT, PPTX, ODT, ODS, ODP, RAR — Download fallback
    return <DownloadFallback fileName={fileName} base64Data={fileSource} />;
};

DocumentViewer.propTypes = {
    documentData: PropTypes.object,
};